import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AdminDashboard.module.css";
import LogoutPopup from "../LogoutPopup/LogoutPopup";
import AdminDocuments from "../AdminDocuments/AdminDocuments";
import AdminUsers from "../AdminUsers/AdminUsers";
import AdminTickets from "../AdminTickets/AdminTickets";
import AdminBookings from "../AdminBookings/AdminBookings";
import AdminAssessments from "../AdminAssessments/AdminAssessments";

const ACTIVE_NAV_KEY = "bvc_admin_active_nav";

function DocIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
  );
}
function TeamIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function TicketIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4z" />
      <path d="M13 5v2" />
      <path d="M13 17v2" />
      <path d="M13 11v2" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function LogoutIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
function AssessmentIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}

const NAV = [
  { key: "documents", label: "Resources", icon: <DocIcon /> },
  { key: "assessments", label: "Assessments", icon: <AssessmentIcon /> },
  { key: "tickets", label: "Tickets", icon: <TicketIcon /> },
  { key: "bookings", label: "Bookings", icon: <CalendarIcon /> },
  { key: "team", label: "User Management", icon: <TeamIcon /> },
];

export default function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const storedUser = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  // Nav gating: full "Admin" sees everything; a "Member" sees only the
  // modules their module_permissions grant "view" on.
  const isSuperAdmin = (storedUser?.role_type || "") === "Admin";
  const modulePerms =
    storedUser?.module_permissions &&
    typeof storedUser.module_permissions === "object"
      ? storedUser.module_permissions
      : {};
  const allowedNav = NAV.filter(
    (item) => isSuperAdmin || modulePerms[item.key]?.view === true,
  );

  const [activeNav, setActiveNav] = useState(() => {
    try {
      return sessionStorage.getItem(ACTIVE_NAV_KEY) || "documents";
    } catch {
      return "documents";
    }
  });

  // Fall back to the first permitted module if the stored/active tab isn't
  // allowed for this user.
  const effectiveNav = allowedNav.some((n) => n.key === activeNav)
    ? activeNav
    : allowedNav[0]?.key || "";

  useEffect(() => {
    if (effectiveNav && effectiveNav !== activeNav) {
      setActiveNav(effectiveNav);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveNav]);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 1024,
  );
  const [sideOpen, setSideOpen] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  );
  const [showLogout, setShowLogout] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close the user menu on outside click.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [userMenuOpen]);

  // Guard — only internal admin-panel accounts (Admin or Member) may be here.
  useEffect(() => {
    const isPanelUser =
      storedUser?.is_admin_panel === true ||
      (storedUser?.role_type || "") === "Admin";
    if (!isPanelUser) {
      navigate("/admin");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const handler = (e) => {
      setIsMobile(e.matches);
      setSideOpen(!e.matches);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(ACTIVE_NAV_KEY, activeNav);
    } catch {
      // Ignore storage failures and keep the current tab state in memory.
    }
  }, [activeNav]);

  const name =
    `${storedUser?.firstname || ""} ${storedUser?.lastname || ""}`.trim() ||
    "Admin";

  const handleLogout = () => {
    sessionStorage.clear();
    if (onLogout) onLogout();
    navigate("/admin");
  };

  const go = (key) => {
    setActiveNav(key);
    if (isMobile) setSideOpen(false);
  };

  return (
    <div className={styles.dashboardWrapper} translate="no">
      <div className={styles.shell}>
        {showLogout && (
          <LogoutPopup
            onConfirm={handleLogout}
            onCancel={() => setShowLogout(false)}
          />
        )}

        <aside
          className={`${styles.sidebar} ${sideOpen ? styles.sideOpen : styles.sideClosed}`}
        >
          <div className={styles.sideLogoRow}>
            <div className={styles.sideLogoMark}>B</div>
            {sideOpen && (
              <div className={styles.sideLogoText}>
                <div className={styles.sideLogoName}>Bruno Vision</div>
                <div className={styles.sideLogoTag}>Admin Panel</div>
              </div>
            )}
          </div>

          {sideOpen && <div className={styles.sideNavLabel}>MANAGE</div>}
          <nav className={styles.sideNav}>
            {allowedNav.map((item) => {
              const active = effectiveNav === item.key;
              return (
                <button
                  key={item.key}
                  className={`${styles.navItem} ${active ? styles.navActive : ""}`}
                  onClick={() => go(item.key)}
                >
                  {active && <span className={styles.navIndicator} />}
                  <span className={styles.navIcon}>{item.icon}</span>
                  {sideOpen && <span className={styles.navLabel}>{item.label}</span>}
                </button>
              );
            })}
          </nav>

          <div className={styles.sideBottom}>
            <button className={styles.logoutBtn} onClick={() => setShowLogout(true)}>
              <span className={styles.navIcon}>
                <LogoutIcon />
              </span>
              {sideOpen && <span className={styles.navLabel}>Logout</span>}
            </button>
          </div>
        </aside>

        {isMobile && sideOpen && (
          <div
            className={styles.sidebarBackdrop}
            onClick={() => setSideOpen(false)}
          />
        )}

        <div className={styles.mainArea}>
          <header className={styles.topBar}>
            <button
              className={styles.toggleBtn}
              onClick={() => setSideOpen((p) => !p)}
              title="Toggle Sidebar"
            >
              <span />
              <span />
              <span />
            </button>
            <div className={styles.topBarRight} ref={userMenuRef}>
              <button
                className={styles.userTrigger}
                onClick={() => setUserMenuOpen((p) => !p)}
                type="button"
              >
                <div className={styles.topAvatar}>
                  {storedUser?.avatar_url ? (
                    <img
                      src={storedUser.avatar_url}
                      alt={name}
                      className={styles.topAvatarImg}
                    />
                  ) : (
                    <>
                      {storedUser?.firstname?.[0] || "A"}
                      {storedUser?.lastname?.[0] || ""}
                    </>
                  )}
                </div>
                <div className={styles.topUserText}>
                  <span className={styles.topName}>{name}</span>
                  <span className={styles.topRole}>Administrator</span>
                </div>
                <svg
                  className={`${styles.userChevron} ${userMenuOpen ? styles.userChevronOpen : ""}`}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {userMenuOpen && (
                <div className={styles.userMenu}>
                  <button
                    className={`${styles.userMenuItem} ${styles.userMenuLogout}`}
                    onClick={() => {
                      setUserMenuOpen(false);
                      setShowLogout(true);
                    }}
                    type="button"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </header>

          <main className={styles.main}>
            {allowedNav.length === 0 && (
              <div style={{ padding: "2rem", color: "#64748b" }}>
                You don't have access to any modules yet. Contact your
                administrator.
              </div>
            )}
            {effectiveNav === "documents" && <AdminDocuments />}
            {effectiveNav === "assessments" && <AdminAssessments />}
            {effectiveNav === "tickets" && <AdminTickets />}
            {effectiveNav === "bookings" && <AdminBookings />}
            {effectiveNav === "team" && <AdminUsers />}
          </main>
        </div>
      </div>
    </div>
  );
}
